import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { useAdmissionDetail } from "../hooks/useAdmissionDetail";
import { updateHospitalAdmissionStatus } from "../api/hospitalApi";
import { sanitizeApiErrorMessage } from "@/shared/lib/apiClient";

type AdmissionDetailPageProps = {
  admissionId: number;
  scope: "company" | "hospital" | "auditor" | "admin";
};

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelation(value: string): string {
  const map: Record<string, string> = {
    spouse: "Conjoint(e)",
    child: "Enfant",
    Employé: "Employé",
  };
  return map[value] ?? (value || "—");
}

export function AdmissionDetailPage({ admissionId, scope }: AdmissionDetailPageProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const admissionLabels = t.safecheck.admissions;
  const listLabels =
    scope === "hospital"
      ? admissionLabels.hospitalList
      : admissionLabels.enterpriseList;
  const { admission, loading, error, reload } = useAdmissionDetail(admissionId, scope);
  const [acting, setActing] = useState(false);

  const homeHref =
    scope === "hospital"
      ? "/dashboard/hospital"
      : scope === "auditor"
        ? "/dashboard/auditor"
        : scope === "admin"
          ? "/dashboard/admin"
          : "/dashboard/enterprise";
  const listHref = "/dashboard/admissions";

  const handleReopen = async () => {
    if (scope !== "hospital" || !admission) return;
    setActing(true);
    try {
      await updateHospitalAdmissionStatus(admission.id, "inProgress");
      showToast("Admission remise en cours.");
      await reload();
    } catch (err) {
      showToast(
        sanitizeApiErrorMessage(
          err instanceof Error ? err.message : "Impossible de remettre en cours.",
        ),
      );
    } finally {
      setActing(false);
    }
  };

  const handleComplete = async () => {
    if (scope !== "hospital" || !admission) return;
    setActing(true);
    try {
      await updateHospitalAdmissionStatus(admission.id, "completed");
      showToast("Admission marquée comme terminée.");
      await reload();
    } catch (err) {
      showToast(
        sanitizeApiErrorMessage(
          err instanceof Error ? err.message : "Impossible de clôturer l'admission.",
        ),
      );
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="theme-card p-8 text-center text-sm text-muted">Chargement de l&apos;admission…</div>
    );
  }

  if (!admission) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted">{error ?? "Admission introuvable."}</p>
        <Link
          to={listHref}
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
        >
          <ArrowLeft size={14} />
          Retour à la liste
        </Link>
      </div>
    );
  }

  const patientName = `${admission.patient} ${admission.patient_sub}`.trim();
  const patientTypeLabel = listLabels.patientTypes[admission.patient_type];
  const statusLabel = admissionLabels.statuses[admission.status];

  return (
    <>
      <Breadcrumbs
        homeHref={homeHref}
        items={[
          { label: t.safecheck.nav.management },
          { label: listLabels.pageTitle, href: listHref },
          { label: `Admission #${admission.id}` },
        ]}
      />

      <div className="theme-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h1 className="text-base font-semibold text-foreground">Détails de l&apos;admission</h1>
            <p className="mt-1 text-sm text-muted">{patientName}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
              {statusLabel}
            </span>
            {scope === "hospital" && admission.status === "completed" ? (
              <button
                type="button"
                disabled={acting}
                onClick={() => void handleReopen()}
                className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-card-muted disabled:opacity-50"
              >
                Remettre en cours
              </button>
            ) : null}
            {scope === "hospital" && admission.status === "inProgress" ? (
              <button
                type="button"
                disabled={acting}
                onClick={() => void handleComplete()}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                Marquer terminée
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-6 p-5 lg:grid-cols-2">
          <section>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
              Informations patient
            </h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">Nom complet</dt>
                <dd className="text-right font-medium text-foreground">{patientName}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">Type</dt>
                <dd className="text-right">
                  {admission.patient_type === "employee" ? (
                    <span className="inline-flex items-center rounded-md bg-purple-soft/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple opacity-90">
                      {patientTypeLabel}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md bg-teal-soft/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal opacity-90">
                      {patientTypeLabel}
                    </span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">ID SYS</dt>
                <dd className="text-right font-mono text-foreground">{admission.employee_id || "—"}</dd>
              </div>
              {admission.patient_type === "beneficiary" && admission.patient_relation && (
                <div className="flex justify-between gap-4 border-b border-border pb-2">
                  <dt className="text-muted">Relation</dt>
                  <dd className="text-right font-medium text-foreground">
                    {formatRelation(admission.patient_relation)}
                  </dd>
                </div>
              )}
            </dl>
          </section>

          <section>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
              Informations admission
            </h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">Date d&apos;admission</dt>
                <dd className="text-right font-medium text-foreground">
                  {admission.date && admission.time
                    ? `${admission.date} ${admission.time}`
                    : admission.date
                      ? admission.date
                      : formatDateTime(admission.date_admission)}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">Date de création</dt>
                <dd className="text-right font-medium text-foreground">
                  {formatDateTime(admission.date_creation)}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">Hôpital</dt>
                <dd className="text-right font-medium text-foreground">{admission.hospital}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted">Entreprise</dt>
                <dd className="text-right font-medium text-foreground">{admission.company}</dd>
              </div>
              {admission.invoice_id ? (
                <div className="flex justify-between gap-4 border-b border-border pb-2">
                  <dt className="text-muted">Facture liée</dt>
                  <dd className="text-right">
                    <Link
                      to={`/dashboard/invoices/${admission.invoice_id}`}
                      className="font-medium text-primary hover:text-primary/80"
                    >
                      #{admission.invoice_id}
                    </Link>
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
        </div>

        {admission.motif ? (
          <div className="border-t border-border px-5 py-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              Motif d&apos;admission
            </h2>
            <p className="text-sm text-foreground">{admission.motif}</p>
          </div>
        ) : null}

        <div className="border-t border-border px-5 py-4">
          <Link
            to={listHref}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary"
          >
            <ArrowLeft size={14} />
            Retour à la liste
          </Link>
        </div>
      </div>
    </>
  );
}
